// ============================================================================
// Géométrie déductive du noyau (lot 5.4) : les PROPOSITIONS d'une ligne.
// ============================================================================
// Une ligne de géométrie est UNE proposition, écrite comme au collège :
//   « (MN) // (BC) »  « (MN) ∦ (BC) »  « (AB) ⊥ (AC) »  « I milieu de [AB] »
//   « J ∈ [AC] »  « M ∈ (AB) »  « M ∈ médiatrice de [AB] »
//   « A ∈ cercle de diamètre [BC] »  « E symétrique de A par rapport à B »
//   « ABCD parallélogramme | rectangle | losange | carré »
//   « ABC rectangle en A »  « ABC isocèle en A »  « ABC n’est pas rectangle »
// ou une relation entre LONGUEURS (« AM ÷ AB = AN ÷ AC », « BC² ≠ AB² + AC² ») —
// celles-ci sont lues par `metric.ts`.
//
// Les motifs des règles (paquet JSON) s'écrivent avec la même syntaxe : un point
// variable « ?A », une droite variable « (?d) ». Pur : aucune dépendance au moteur.
// ============================================================================

export type Pt = string;
/** une droite : par deux points « (AB) », ou nommée « (d₁) » (motif : « (?d) ») */
export type LineRef = { k: 'pts'; a: Pt; b: Pt } | { k: 'name'; n: string };

export type QuadKind = 'para' | 'rectangle' | 'losange' | 'carre';
export type GProp =
  | { k: 'par' | 'npar' | 'perp'; a: LineRef; b: LineRef }
  /** p milieu de [ab] ; p ∈ [ab] ; p ∈ médiatrice de [ab] ; p ∈ cercle de diamètre [ab] */
  | { k: 'mid' | 'onseg' | 'bis' | 'circ'; p: Pt; a: Pt; b: Pt }
  | { k: 'online'; p: Pt; l: LineRef }
  | { k: QuadKind; v: Pt[] }
  /** triangle (at, b, c) rectangle / isocèle en `at` */
  | { k: 'rt' | 'iso'; at: Pt; b: Pt; c: Pt }
  | { k: 'nrt'; v: Pt[] };

export class GeometrySyntaxError extends Error {}

/** les relations de la palette (bouts), dans l'ordre d'affichage */
export const GEO_RELATIONS = [
  '//',
  '∦',
  '⊥',
  '∈',
  'milieu de',
  'médiatrice de',
  'cercle de diamètre',
  'symétrique de',
  'par rapport à',
  'parallélogramme',
  'rectangle',
  'losange',
  'carré',
  'rectangle en',
  'isocèle en',
  'n’est pas rectangle',
] as const;
const QUAD_WORD: Record<QuadKind, string> = {
  para: 'parallélogramme',
  rectangle: 'rectangle',
  losange: 'losange',
  carre: 'carré',
};
const WORD_QUAD: Record<string, QuadKind> = {
  parallélogramme: 'para',
  rectangle: 'rectangle',
  losange: 'losange',
  carré: 'carre',
};

/* ------------------------------ lecture ------------------------------ */

/** espaces et apostrophes normalisés, « ∥ » écrit « // », une espace autour de // ∦ ⊥ ∈ */
export function normalizeGeoText(text: string): string {
  return text
    .replace(/[\u2009\u202f\u00a0]/g, ' ')
    .replace(/∥/g, '//')
    .replace(/'/g, '’')
    .replace(/\s*(\/\/|∦|⊥|∈)\s*/g, ' $1 ')
    .replace(/\s+/g, ' ')
    .trim();
}

const POINT_RE = /\??[A-Z][0-9]*/g;
/** « ABC » → [A, B, C] ; « ?A?B » → [?A, ?B] (null si ce n'est pas une suite de points) */
function pointsOf(s: string, pattern: boolean): Pt[] | null {
  const pts = s.match(POINT_RE) ?? [];
  if (pts.join('') !== s || !pts.length) return null;
  if (pts.some((p) => (pattern ? !p.startsWith('?') && p.length > 1 : p.length !== 1))) return null;
  if (!pattern && pts.some((p) => p.startsWith('?'))) return null;
  return pts;
}
function onePoint(s: string, pattern: boolean): Pt {
  const p = pointsOf(s, pattern);
  if (!p || p.length !== 1)
    throw new GeometrySyntaxError('Un point s’écrit avec une lettre majuscule : A, B, M…');
  return p[0]!;
}
const distinct = (xs: string[]): boolean => new Set(xs).size === xs.length;

function lineOf(s: string, pattern: boolean): LineRef {
  const m = /^\((.+)\)$/.exec(s);
  if (!m) throw new GeometrySyntaxError('Une droite s’écrit entre parenthèses : (AB) ou (d₁).');
  const inner = m[1]!;
  const pts = pointsOf(inner, pattern);
  if (pts && pts.length === 2) {
    if (pts[0] === pts[1])
      throw new GeometrySyntaxError('Une droite passe par deux points différents.');
    return { k: 'pts', a: pts[0]!, b: pts[1]! };
  }
  const nameRe = pattern ? /^(\?[a-z][0-9]*|[a-zΔ][₀-₉]*)$/ : /^[a-zΔ][₀-₉]*$/;
  if (nameRe.test(inner)) return { k: 'name', n: inner };
  throw new GeometrySyntaxError('Une droite s’écrit entre parenthèses : (AB) ou (d₁).');
}
function segmentOf(s: string, pattern: boolean): [Pt, Pt] {
  const m = /^\[(.+)\]$/.exec(s);
  const pts = m ? pointsOf(m[1]!, pattern) : null;
  if (!pts || pts.length !== 2)
    throw new GeometrySyntaxError('Un segment s’écrit entre crochets : [AB].');
  if (pts[0] === pts[1]) throw new GeometrySyntaxError('Un segment a deux extrémités différentes.');
  return [pts[0]!, pts[1]!];
}
function pointNotIn(p: Pt, ends: Pt[]): void {
  if (ends.includes(p))
    throw new GeometrySyntaxError('Le point doit être différent des extrémités du segment.');
}

/** La ligne est-elle une proposition de géométrie (et pas une relation entre longueurs) ? */
export function looksGeometric(text: string): boolean {
  const s = normalizeGeoText(text);
  return (
    /(^|\s)(\/\/|∦|⊥|∈)(\s|$)/.test(s) ||
    /[[\]]/.test(s) ||
    /\((?:[A-Z?][A-Z0-9?]*|[a-zΔ?][₀-₉0-9a-z]*)\)/.test(s) ||
    /(milieu|symétrique|parallélogramme|rectangle|losange|carré|isocèle|médiatrice|cercle)/.test(s)
  );
}

/**
 * Lit une proposition. `pattern` : motif d'une règle (points « ?A », droites « (?d) »).
 * Lève `GeometrySyntaxError` avec un message pour l'élève.
 */
export function parseGProp(text: string, pattern = false): GProp {
  const s = normalizeGeoText(text);
  let m = /^(\S+) (\/\/|∦|⊥) (\S+)$/.exec(s);
  if (m) {
    const a = lineOf(m[1]!, pattern);
    const b = lineOf(m[3]!, pattern);
    if (lineText(a) === lineText(b) || lineText(a) === lineText(swapLine(b)))
      throw new GeometrySyntaxError('Il faut deux droites différentes.');
    return { k: m[2] === '//' ? 'par' : m[2] === '∦' ? 'npar' : 'perp', a, b };
  }
  m = /^(\S+) (?:est (?:le )?)?milieu (?:de|du segment) (\S+)$/.exec(s);
  if (m) {
    const p = onePoint(m[1]!, pattern);
    const [a, b] = segmentOf(m[2]!, pattern);
    pointNotIn(p, [a, b]);
    return { k: 'mid', p, a, b };
  }
  m = /^(\S+) ∈ (?:la )?médiatrice (?:de|du segment) (\S+)$/.exec(s);
  if (m) {
    const p = onePoint(m[1]!, pattern);
    const [a, b] = segmentOf(m[2]!, pattern);
    pointNotIn(p, [a, b]);
    return { k: 'bis', p, a, b };
  }
  m = /^(\S+) ∈ (?:le )?cercle de diamètre (\S+)$/.exec(s);
  if (m) {
    const p = onePoint(m[1]!, pattern);
    const [a, b] = segmentOf(m[2]!, pattern);
    pointNotIn(p, [a, b]);
    return { k: 'circ', p, a, b };
  }
  m = /^(\S+) ∈ (\S+)$/.exec(s);
  if (m) {
    const p = onePoint(m[1]!, pattern);
    if (m[2]!.startsWith('[')) {
      const [a, b] = segmentOf(m[2]!, pattern);
      pointNotIn(p, [a, b]);
      return { k: 'onseg', p, a, b };
    }
    const l = lineOf(m[2]!, pattern);
    if (l.k === 'pts') pointNotIn(p, [l.a, l.b]);
    return { k: 'online', p, l };
  }
  m = /^(\S+) (?:est )?(?:le )?symétrique de (\S+) par rapport à (\S+)$/.exec(s);
  if (m) {
    const e = onePoint(m[1]!, pattern);
    const a = onePoint(m[2]!, pattern);
    const o = onePoint(m[3]!, pattern);
    if (!distinct([e, a, o])) throw new GeometrySyntaxError('Il faut trois points différents.');
    // E symétrique de A par rapport à O : O milieu de [AE]
    return { k: 'mid', p: o, a, b: e };
  }
  m = /^(\S+) (?:est )?(?:un triangle )?(rectangle|isocèle) en (\S+)$/.exec(s);
  if (m) {
    const v = pointsOf(m[1]!, pattern);
    const at = onePoint(m[3]!, pattern);
    if (!v || v.length !== 3 || !distinct(v))
      throw new GeometrySyntaxError('Un triangle s’écrit avec trois points : ABC.');
    if (!v.includes(at))
      throw new GeometrySyntaxError(
        `Le sommet ${at} doit être un sommet du triangle ${v.join('')}.`,
      );
    const [b, c] = v.filter((x) => x !== at);
    return { k: m[2] === 'rectangle' ? 'rt' : 'iso', at, b: b!, c: c! };
  }
  m = /^(\S+) n’est pas (?:un triangle )?rectangle$/.exec(s);
  if (m) {
    const v = pointsOf(m[1]!, pattern);
    if (!v || v.length !== 3 || !distinct(v))
      throw new GeometrySyntaxError('Un triangle s’écrit avec trois points : ABC.');
    return { k: 'nrt', v };
  }
  m = /^(\S+) (?:est (?:un )?)?(parallélogramme|rectangle|losange|carré)$/.exec(s);
  if (m) {
    const v = pointsOf(m[1]!, pattern);
    if (!v || v.length !== 4 || !distinct(v))
      throw new GeometrySyntaxError(
        'Un quadrilatère s’écrit avec ses quatre sommets dans l’ordre : ABCD.',
      );
    return { k: WORD_QUAD[m[2]!]!, v };
  }
  throw new GeometrySyntaxError(
    'Écris une seule propriété : « (MN) // (BC) », « I milieu de [AB] », « ABCD parallélogramme », « ABC rectangle en A »…',
  );
}

/* ------------------------------ écriture ------------------------------ */

function swapLine(l: LineRef): LineRef {
  return l.k === 'pts' ? { k: 'pts', a: l.b, b: l.a } : l;
}
export function lineText(l: LineRef): string {
  return l.k === 'pts' ? `(${l.a}${l.b})` : `(${l.n})`;
}
export function gpropText(p: GProp): string {
  switch (p.k) {
    case 'par':
      return `${lineText(p.a)} // ${lineText(p.b)}`;
    case 'npar':
      return `${lineText(p.a)} ∦ ${lineText(p.b)}`;
    case 'perp':
      return `${lineText(p.a)} ⊥ ${lineText(p.b)}`;
    case 'mid':
      return `${p.p} milieu de [${p.a}${p.b}]`;
    case 'onseg':
      return `${p.p} ∈ [${p.a}${p.b}]`;
    case 'bis':
      return `${p.p} ∈ médiatrice de [${p.a}${p.b}]`;
    case 'circ':
      return `${p.p} ∈ cercle de diamètre [${p.a}${p.b}]`;
    case 'online':
      return `${p.p} ∈ ${lineText(p.l)}`;
    case 'para':
    case 'rectangle':
    case 'losange':
    case 'carre':
      return `${p.v.join('')} ${QUAD_WORD[p.k]}`;
    case 'rt':
      return `${p.at}${p.b}${p.c} rectangle en ${p.at}`;
    case 'iso':
      return `${p.at}${p.b}${p.c} isocèle en ${p.at}`;
    case 'nrt':
      return `${p.v.join('')} n’est pas rectangle`;
  }
}

/** Les points nommés dans une proposition (variables comprises). */
export function gpropPoints(p: GProp): Pt[] {
  const ln = (l: LineRef): Pt[] => (l.k === 'pts' ? [l.a, l.b] : []);
  switch (p.k) {
    case 'par':
    case 'npar':
    case 'perp':
      return [...ln(p.a), ...ln(p.b)];
    case 'mid':
    case 'onseg':
    case 'bis':
    case 'circ':
      return [p.p, p.a, p.b];
    case 'online':
      return [p.p, ...ln(p.l)];
    case 'rt':
    case 'iso':
      return [p.at, p.b, p.c];
    default:
      return [...p.v];
  }
}
/** Les droites nommées (« d₁ », variables « ?d ») d'une proposition. */
export function gpropLineNames(p: GProp): string[] {
  const ln = (l: LineRef): string[] => (l.k === 'name' ? [l.n] : []);
  if (p.k === 'par' || p.k === 'npar' || p.k === 'perp') return [...ln(p.a), ...ln(p.b)];
  if (p.k === 'online') return ln(p.l);
  return [];
}

/* ------------------------------ clé syntaxique ------------------------------ */

const sortPair = (a: string, b: string): string => (a < b ? a + b : b + a);
function lineKeySyntax(l: LineRef): string {
  return l.k === 'pts' ? `(${sortPair(l.a, l.b)})` : `(${l.n})`;
}
/** sommets d'un quadrilatère : le plus petit des 8 parcours (rotations, sens) */
export function cycleKey(v: Pt[]): string {
  const out: string[] = [];
  for (let i = 0; i < v.length; i++) {
    const r = [...v.slice(i), ...v.slice(0, i)];
    out.push(r.join(''), [...r].reverse().join(''));
  }
  return out.sort()[0]!;
}
/** Clé d'une proposition, sans tenir compte des alignements (pièges, choix du QCM). */
export function gpropKey(p: GProp): string {
  switch (p.k) {
    case 'par':
    case 'npar':
    case 'perp': {
      const [x, y] = [lineKeySyntax(p.a), lineKeySyntax(p.b)].sort();
      return `${p.k} ${x} ${y}`;
    }
    case 'mid':
    case 'onseg':
    case 'bis':
    case 'circ':
      return `${p.k} ${p.p} ${sortPair(p.a, p.b)}`;
    case 'online':
      return `online ${p.p} ${lineKeySyntax(p.l)}`;
    case 'rt':
    case 'iso':
      return `${p.k} ${p.at} ${sortPair(p.b, p.c)}`;
    case 'nrt':
      return `nrt ${[...p.v].sort().join('')}`;
    default:
      return `${p.k} ${cycleKey(p.v)}`;
  }
}

/* ------------------------------ bouts ------------------------------ */

const REL_PHRASES = [...GEO_RELATIONS].sort((a, b) => b.length - a.length);
/**
 * Les bouts d'une proposition écrite (affichage de l'énoncé, palette, coup de pouce) :
 * « I milieu de [AB] » → [I, milieu de, [AB]]. Les mots facultatifs (« est », « le »)
 * sont retirés ; le texte doit être une proposition lisible.
 */
export function gpropChips(text: string): string[] {
  let s = normalizeGeoText(text)
    .replace(/ est (?:le |la |un )?/g, ' ')
    .replace(/ ∈ (?:la |le )/g, ' ∈ ')
    .replace(/ n’est pas (?:un triangle )?rectangle$/, ' n’est pas rectangle')
    .replace(/ un triangle /g, ' ')
    .replace(/ du segment /g, ' de ');
  const out: string[] = [];
  while (s.length) {
    s = s.trimStart();
    const rel = REL_PHRASES.find(
      (r) => s.startsWith(r) && (s.length === r.length || s[r.length] === ' '),
    );
    if (rel) {
      out.push(rel);
      s = s.slice(rel.length);
      continue;
    }
    const w = /^\S+/.exec(s)![0];
    out.push(w);
    s = s.slice(w.length);
  }
  return out;
}
